import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useDomainMutation } from "@/modules/common/hooks/domain-mutation.hook";
import { invalidateCustomerContactQueries } from "@/modules/common/utils/customer-contact-cache";
import {
  getContactConflict,
  resolveContactConflict,
} from "../../api/contact-conflicts.api";
import {
  useContactConflictLink,
  usePendingContactConflicts,
} from "../../hooks/contact-conflicts.hook";
import {
  contactConflictDestination,
  contactConflictError,
  isContactConflictUid,
  selectedConflictWinners,
} from "../../utils/contact-conflicts";
import type { ResolveContactConflictRequest } from "../../dtos/contact-conflicts";
import { ContactConflictDetails } from "./preview";

/** Mounted once per lead page so notification links work independently of action menus. */
export function ContactConflictResolver() {
  const { allowed, data: pending } = usePendingContactConflicts();
  const { conflictUid, openConflict, closeConflict } = useContactConflictLink();
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [confirmedRevision, setConfirmedRevision] = useState<string | null>(
    null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const open = !!conflictUid;
  const preview = useQuery({
    queryKey: ["contact-conflict", conflictUid],
    queryFn: () => getContactConflict(conflictUid!),
    enabled: open && allowed && isContactConflictUid(conflictUid),
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });
  const data = allowed ? preview.data : undefined;

  useEffect(() => {
    setSelections({});
    setConfirmedRevision(null);
  }, [conflictUid, data?.revision]);
  useEffect(() => setNotice(null), [conflictUid]);

  const resolution = useDomainMutation({
    mutationFn: (request: ResolveContactConflictRequest) =>
      resolveContactConflict(conflictUid!, request),
    onSuccess: async (result) => {
      await Promise.all([
        invalidateCustomerContactQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: ["contact-conflicts"] }),
      ]);
      navigate(contactConflictDestination(result.redirectTo), {
        replace: true,
      });
    },
    onError: async (error) => {
      const failure = contactConflictError(error);
      setNotice(failure.message);
      setConfirmedRevision(null);
      if (failure.stale) {
        setSelections({});
        await preview.refetch();
      }
    },
  });
  const winners = data
    ? selectedConflictWinners(data.groups, selections)
    : null;
  const confirmed = !!data?.revision && confirmedRevision === data.revision;
  const canConfirm =
    allowed &&
    data?.status === "pending" &&
    confirmed &&
    winners !== null &&
    !preview.isFetching &&
    !preview.isError &&
    !resolution.isLoading;
  const confirm = () => {
    if (canConfirm && data && winners)
      resolution.mutate({
        revision: data.revision,
        winningJourneyUIds: winners,
      });
  };
  const close = () => {
    if (!resolution.isLoading) closeConflict();
  };
  const ModalWrapper = isMobile ? MobileModal : CommonModal;
  if (!open) return null;
  return (
    <ModalWrapper
      open={open}
      onClose={close}
      title="Resolve contact conflict"
      cancelButtonText="Close"
      confirmButtonText={
        data?.status === "pending" ? "Confirm merge" : undefined
      }
      onConfirm={confirm}
      isConfirmDisabled={!canConfirm}
      isLoading={resolution.isLoading}
      sx={
        isMobile
          ? undefined
          : { width: "min(760px, calc(100vw - 32px))", maxHeight: "90vh" }
      }
    >
      <Stack
        spacing={2}
        sx={{
          p: isMobile ? 0 : 3,
          overflowY: "auto",
          minHeight: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {!allowed ? (
          <Alert severity="error">
            You need Merge Lead permission to resolve this contact conflict.
            Please contact Operations.
          </Alert>
        ) : !isContactConflictUid(conflictUid) ? (
          <Alert severity="error">This contact conflict link is invalid.</Alert>
        ) : (
          <>
            {pending && pending.length > 1 && (
              <TextField
                select
                label="Pending conflict"
                value={
                  pending.some((item) => item.uid === conflictUid)
                    ? conflictUid
                    : ""
                }
                onChange={(event) => openConflict(event.target.value)}
                disabled={resolution.isLoading}
                fullWidth
                size="small"
              >
                {pending.map((item, index) => (
                  <MenuItem key={item.uid} value={item.uid}>
                    Conflict {index + 1} ·{" "}
                    {new Date(item.createdAt).toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {notice && (
              <Alert severity="warning" role="alert">
                {notice}
              </Alert>
            )}
            {preview.isFetching && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress
                  size={28}
                  aria-label="Loading contact conflict"
                />
              </Box>
            )}
            {preview.isError && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void preview.refetch()}
                  >
                    Retry
                  </Button>
                }
              >
                {contactConflictError(preview.error).message}
              </Alert>
            )}
            {data?.status === "resolved" && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    onClick={() =>
                      navigate(contactConflictDestination(data.redirectTo), {
                        replace: true,
                      })
                    }
                  >
                    Open lead
                  </Button>
                }
              >
                This conflict has already been resolved.
              </Alert>
            )}
            {data?.status === "pending" && !preview.isError && (
              <>
                <ContactConflictDetails
                  preview={data}
                  selections={selections}
                  disabled={resolution.isLoading || preview.isFetching}
                  onSelect={(key, value) => {
                    setSelections((current) => ({ ...current, [key]: value }));
                    setConfirmedRevision(null);
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      disabled={
                        winners === null ||
                        resolution.isLoading ||
                        preview.isFetching
                      }
                      onChange={(_, checked) =>
                        setConfirmedRevision(checked ? data.revision : null)
                      }
                    />
                  }
                  label="I confirm these are the same customer and approve the leads and agents selected above."
                  sx={{
                    alignItems: "flex-start",
                    "& .MuiCheckbox-root": { pt: 0 },
                  }}
                />
              </>
            )}
          </>
        )}
      </Stack>
    </ModalWrapper>
  );
}
